import type { BookingResponse } from "../types/generated";
import { toCents } from "./money";

/**
 * Group a flat list of bookings into "sets" so a multi-court / recurring
 * reservation (many `court_bookings` sharing ONE `recurring_group_id` from a
 * single batch checkout) presents as a single unit instead of N loose rows.
 *
 * Pure presentation grouping — no derived business state. Every displayed value
 * still comes from a real server field (`amount_charged`, `start_time`, `court_id`,
 * `court_name`, `currency`); nothing is fabricated. `display_name` is the
 * booked-for player's name and is never used as a court label.
 */

/** One reservation "set": a recurring group, or a singleton for a null group. */
export interface BookingSet {
  /** The set's stable key: `recurring_group_id`, or the `booking_id` for a
   *  null-group singleton (each batch's group uuid is unique, so no two distinct
   *  reservations ever merge). */
  key: string;
  /** True when the set holds MORE THAN ONE booking (a multi-booking reservation).
   *  A single-booking set is false → the caller renders the normal single row. */
  isGroup: boolean;
  /** The bookings in this set, preserving the incoming order. */
  bookings: BookingResponse[];
  /** The representative (anchor) booking = the EARLIEST start_time. The set's
   *  "View" deep link + representative facility come from this. */
  anchor: BookingResponse;
}

/** ms since epoch for a booking's start, or +Infinity for an unparseable one
 *  (so a bad row sorts LAST and never becomes the anchor over a real one). */
function startMs(b: BookingResponse): number {
  const ms = Date.parse(b.start_time);
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

/**
 * Collapse `bookings` into `BookingSet[]`. Bookings sharing a NON-NULL
 * `recurring_group_id` collapse into ONE set positioned at the group's FIRST
 * appearance; a NULL-group booking is its own singleton set (keyed by booking_id).
 * Incoming order is preserved; each set's anchor is its earliest-start booking.
 */
export function groupBookingSets(bookings: BookingResponse[]): BookingSet[] {
  const sets: BookingSet[] = [];
  const indexByGroup = new Map<string, number>();

  for (const b of bookings) {
    const gid = b.recurring_group_id ?? null;
    if (!gid) {
      sets.push({
        key: b.booking_id,
        isGroup: false,
        bookings: [b],
        anchor: b,
      });
      continue;
    }
    const existing = indexByGroup.get(gid);
    if (existing === undefined) {
      indexByGroup.set(gid, sets.length);
      sets.push({ key: gid, isGroup: false, bookings: [b], anchor: b });
    } else {
      sets[existing].bookings.push(b);
    }
  }

  // Finalize: mark multi-booking sets + pick the earliest-start anchor.
  return sets.map((s) => {
    if (s.bookings.length <= 1) return s;
    let anchor = s.bookings[0];
    for (const b of s.bookings) {
      if (startMs(b) < startMs(anchor)) anchor = b;
    }
    return { ...s, isGroup: true, anchor };
  });
}

export type BookingSetKind = "single" | "multi-court" | "recurring" | "generic";

/** A structured, presentation-ready descriptor of a set (no formatting). */
export interface BookingSetSummary {
  /** The adaptive shape: a lone booking, a same-time multi-court reservation, a
   *  same-court recurring series, or a mixed "generic" set. */
  kind: BookingSetKind;
  /** Number of bookings in the set. */
  sessionCount: number;
  /** Distinct `court_id`s in the set. */
  courtCount: number;
  /** Server-resolved court name when every booking belongs to one named court. */
  courtName: string | null;
  /** Earliest start_time (the anchor slot's start). */
  earliestStart: string;
  /** end_time of the earliest-start (anchor) booking. */
  anchorEnd: string;
  /** Latest start_time in the set. */
  latestStart: string;
  /** Total charged across the set in integer CENTS (Σ amount_charged), summed via
   *  `toCents` so float drift never mis-totals money. */
  totalCents: number;
  /** The set's currency (the first booking's). */
  currency: string;
}

/** Derive the structured summary for a set's bookings (real fields only). */
export function summarizeBookingSet(
  bookings: BookingResponse[],
): BookingSetSummary {
  const sessionCount = bookings.length;
  const courtCount = new Set(bookings.map((b) => b.court_id)).size;
  const courtNames = new Set(
    bookings.map((b) => b.court_name?.trim()).filter(Boolean),
  );
  const startTimes = bookings.map((b) => b.start_time);
  const distinctStarts = new Set(startTimes).size;
  // Date key = the ISO date prefix (YYYY-MM-DD) of each start — distinct per day
  // for a same-time, different-day recurring series.
  const distinctDates = new Set(startTimes.map((s) => s.slice(0, 10))).size;

  let anchor = bookings[0];
  let latest = bookings[0];
  for (const b of bookings) {
    if (startMs(b) < startMs(anchor)) anchor = b;
    if (startMs(b) > startMs(latest)) latest = b;
  }

  const totalCents = bookings.reduce(
    (acc, b) => acc + (toCents(b.amount_charged) ?? 0),
    0,
  );
  const currency = bookings[0]?.currency ?? "USD";

  let kind: BookingSetKind;
  if (sessionCount <= 1) {
    kind = "single";
  } else if (distinctStarts === 1 && courtCount > 1) {
    // All rows share one start_time but DISTINCT courts → a multi-court booking.
    kind = "multi-court";
  } else if (courtCount === 1 && distinctDates > 1) {
    // All rows share one court but DISTINCT dates → a recurring series.
    kind = "recurring";
  } else {
    kind = "generic";
  }

  return {
    kind,
    sessionCount,
    courtCount,
    courtName:
      courtCount === 1 && courtNames.size === 1
        ? (courtNames.values().next().value ?? null)
        : null,
    earliestStart: anchor.start_time,
    anchorEnd: anchor.end_time,
    latestStart: latest.start_time,
    totalCents,
    currency,
  };
}

// ---- date/time formatting (best-effort, never leaks "Invalid Date") ---------

function fmtDate(iso: string): string {
  try {
    const f = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(new Date(iso));
    return /invalid/i.test(f) ? "" : f;
  } catch {
    return "";
  }
}

function fmtShortDate(iso: string): string {
  try {
    const f = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(new Date(iso));
    return /invalid/i.test(f) ? "" : f;
  } catch {
    return "";
  }
}

function fmtTime(iso: string): string {
  try {
    const f = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(iso));
    return /invalid/i.test(f) ? "" : f;
  } catch {
    return "";
  }
}

/**
 * The adaptive one-line label for a set:
 *   - multi-court: `"3 courts · Sat, Jun 20 · 5:00 PM – 6:00 PM"`
 *   - recurring:   `"Court 7 · 4 sessions · Jun 20 – Jul 11"`
 *   - generic:     `"5 bookings"`
 *   - single:      the server-resolved court name, else the neutral `"Booking"`
 *
 * Court identity comes only from the summary's canonical
 * `BookingResponse.court_name`; the booker's `display_name` is never used.
 */
export function formatBookingSetLabel(s: BookingSetSummary): string {
  if (s.kind === "multi-court") {
    const date = fmtDate(s.earliestStart);
    const range = `${fmtTime(s.earliestStart)} – ${fmtTime(s.anchorEnd)}`;
    const tail = [date, range].filter(Boolean).join(" · ");
    return tail ? `${s.courtCount} courts · ${tail}` : `${s.courtCount} courts`;
  }
  if (s.kind === "recurring") {
    const court = s.courtName || "Court";
    const range = [fmtShortDate(s.earliestStart), fmtShortDate(s.latestStart)]
      .filter(Boolean)
      .join(" – ");
    const base = `${court} · ${s.sessionCount} sessions`;
    return range ? `${base} · ${range}` : base;
  }
  if (s.kind === "generic") {
    return `${s.sessionCount} bookings`;
  }
  return s.courtName || "Booking";
}
